import { createContext, useContext } from "react";

/**
 * Context type for portal containers
 */
interface PortalContextType {
	/**
	 * The DOM element that serves as the portal container
	 */
	container: HTMLElement | null;
	/**
	 * Depth level for nested portals (future extensibility)
	 */
	depth: number;
}

/**
 * Context for tracking portal containers throughout the component tree.
 * This allows components like Tooltip to automatically detect when they're
 * inside a portal and render within the same container for proper layering.
 */
export const PortalContext = createContext<PortalContextType | null>(null);

/**
 * Hook to access the current portal container from context.
 * Returns an object with container property for future extensibility.
 * Returns null if not within a portal context.
 *
 * @returns Object with container property or null if not in a portal context
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const portalInfo = usePortalContainer();
 *
 *   return (
 *     <Tooltip portalContainer={portalInfo?.container}>
 *       <Button>Hover me</Button>
 *     </Tooltip>
 *   );
 * };
 * ```
 */
export const usePortalContainer = (): { container: HTMLElement | null } | null => {
	const context = useContext(PortalContext);
	return context ? { container: context.container } : null;
};

/**
 * Hook to access the full portal context information.
 * Returns null if not within a portal context.
 *
 * @returns The full portal context or null if not in a portal context
 */
export const usePortalContext = (): PortalContextType | null => {
	const context = useContext(PortalContext);
	return context;
};
